import {
  exportReport,
  fetchReport,
  Market,
  ReportFilters,
} from "api/admin/referrals";
import Table from "components/ui/Table";
import useAdminAccess from "hooks/useAdminAccess";
import { FormEvent, useState } from "react";
import { useMutation, useQuery } from "react-query";
import {
  Button,
  Card,
  Field,
  UserLink,
  dateWindow,
  downloadCsv,
  errorMessage,
  fieldClass,
  markets,
  money,
  queryOptions,
  useReferralKey,
} from "./shared";
export default function Reports() {
  const [from, setFrom] = useState(""),
    [to, setTo] = useState(""),
    [market, setMarket] = useState("");
  const [filters, setFilters] = useState<ReportFilters>(),
    [error, setError] = useState("");
  const key = useReferralKey(),
    { can } = useAdminAccess();
  const query = useQuery(
    [...key, "report", filters],
    () => fetchReport(filters!),
    { ...queryOptions, enabled: Boolean(filters) },
  );
  const csv = useMutation(exportReport, {
    retry: false,
    onSuccess: (text) => downloadCsv(text, "referral-report.csv"),
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError("");
    try {
      setFilters({
        ...dateWindow(from, to),
        market: (market as Market) || undefined,
      });
      csv.reset();
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  return (
    <div className="space-y-5">
      <Card
        title="Referral reports"
        description="Event-date totals, with rewards kept separate by payout currency. Review holds and balances show their current state."
      >
        <form onSubmit={submit} className="p-5">
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="From (UTC)">
              <input
                required
                type="date"
                className={fieldClass}
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </Field>
            <Field label="Before (UTC, exclusive)">
              <input
                required
                type="date"
                className={fieldClass}
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </Field>
            <Field label="Market">
              <select
                className={fieldClass}
                value={market}
                onChange={(e) => setMarket(e.target.value)}
              >
                <option value="">All markets</option>
                {markets.map((m) => (
                  <option key={m}>{m}</option>
                ))}
              </select>
            </Field>
          </div>
          <p className="mt-3 text-xs text-gray-500">
            Choose a window of up to 366 days. The end date is excluded. For
            October 1–31, use November 1 as the end date.
          </p>
          <div className="flex flex-wrap gap-3 mt-4">
            <Button type="submit">Run report</Button>
            {can("referrals.reports.export") && (
              <Button
                type="button"
                variant="secondary"
                disabled={
                  !filters || csv.isLoading || query.isFetching || query.isError
                }
                onClick={() => filters && csv.mutate(filters)}
              >
                {csv.isLoading ? "Exporting…" : "Export report CSV"}
              </Button>
            )}
          </div>
          {(error || csv.isError) && (
            <p role="alert" className="mt-3 text-sm text-red-700">
              {error || errorMessage(csv.error)}
            </p>
          )}
        </form>
      </Card>
      {!filters ? (
        <p className="text-sm text-gray-500">Choose dates to run a report.</p>
      ) : (
        <>
          <p className="text-sm">
            Showing {filters.from.slice(0, 10)} up to {filters.to.slice(0, 10)}{" "}
            (exclusive), {filters.market ?? "all markets"}.
          </p>
          <div className="overflow-x-auto">
            <Table
              header={[
                "Market",
                "Sign-ups",
                "Successful",
                "Capped",
                "Rejected",
                "On hold now",
                "Rewards",
              ]}
              isLoading={query.isLoading}
              errorMessage={
                query.isError ? errorMessage(query.error) : undefined
              }
              onRetry={() => void query.refetch()}
              emptyMessage="No report data for this window."
              data={(query.data?.markets ?? []).map((row) => [
                row.market,
                row.signUps,
                row.successful,
                row.capped,
                row.rejected,
                row.onHoldNow,
                <ul className="min-w-[200px] space-y-3">
                  {row.rewards.length ? (
                    row.rewards.map((reward) => (
                      <li key={reward.currency}>
                        Gross: {money(reward.grossMinor, reward.currency)}
                        <br />
                        Reversed: {money(
                          reward.reversedMinor,
                          reward.currency,
                        )}{" "}
                        ({reward.reversedCount})<br />
                        Net: {money(reward.netMinor, reward.currency)}
                      </li>
                    ))
                  ) : (
                    <li>No rewards in this window</li>
                  )}
                </ul>,
                undefined,
              ])}
            />
          </div>
          {query.data && !query.isError && (
            <Card
              title="Top referrers"
              description="Up to 50 referrers, ranked by rewards credited in the reporting window."
            >
              <div className="overflow-x-auto">
                <Table
                  header={[
                    "Referrer",
                    "Market",
                    "Successful",
                    "Capped",
                    "Rejected",
                    "Net rewards",
                    "Current balance",
                  ]}
                  emptyMessage="No rewarded referrers in this window."
                  data={query.data.topReferrers.map((row) => [
                    <UserLink id={row.referrerId}>
                      {row.userName ||
                        row.firstName ||
                        `User #${row.referrerId}`}
                    </UserLink>,
                    row.market,
                    row.successful,
                    row.capped,
                    row.rejected,
                    row.balanceCurrency
                      ? money(row.netRewardMinor, row.balanceCurrency)
                      : "—",
                    row.balanceCurrency && row.balanceMinor !== null
                      ? money(row.balanceMinor, row.balanceCurrency)
                      : "No credits",
                    undefined,
                  ])}
                />
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
